import {
  IonAlert,
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonModal,
  IonPage,
  IonTitle,
  IonToast,
  IonToolbar,
} from "@ionic/react";
import { add, chevronBack, copyOutline } from "ionicons/icons";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { apiFetch } from "../api/client.js";
import { fetchTeams } from "../api/teams.js";
import { clearUsersCache, fetchUsers } from "../api/users.js";

const blank = { name: "", employeeNumber: "", email: "", hourlyRate: 0 };

function LastActive({ value }) {
  const date = value ? new Date(value) : null;
  const isValid = date && Number.isFinite(date.getTime());

  return (
    <div className="user-last-active">
      <span>Last active</span>
      {isValid ? (
        <time dateTime={value}>
          <strong>{new Intl.DateTimeFormat("en-AU", { dateStyle: "medium" }).format(date)}</strong>
          <small>{new Intl.DateTimeFormat("en-AU", { timeStyle: "short" }).format(date)}</small>
        </time>
      ) : <strong>Never</strong>}
    </div>
  );
}

export default function TeamUsersPage() {
  const history = useHistory();
  const { teamId } = useParams();
  const stateTeam = history.location.state?.team;
  const [team, setTeam] = useState(stateTeam || null);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(blank);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editHourlyRate, setEditHourlyRate] = useState(0);
  const [toast, setToast] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const load = useCallback(async () => {
    const [teamList, userList] = await Promise.all([fetchTeams(), fetchUsers({ teamId, force: true })]);
    setTeam(teamList.find((item) => item.id === teamId) || null);
    setUsers(userList);
  }, [teamId]);

  useEffect(() => {
    localStorage.setItem("metora_team_id", teamId);
    load().catch((err) => setToast(err.message));
  }, [load, teamId]);

  function inviteCode(user) {
    return team ? `${team.code}/${user.userCode}` : user.userCode;
  }

  async function copyCode(user) {
    try {
      await navigator.clipboard.writeText(inviteCode(user));
      setToast("Invite code copied.");
    } catch {
      setToast("Unable to copy invite code.");
    }
  }

  async function save() {
    try {
      const user = await apiFetch("/users", { method: "POST", body: form, teamId });
      clearUsersCache(teamId);
      setForm(blank);
      setIsCreateOpen(false);
      await load();
      await navigator.clipboard.writeText(user.inviteCode).catch(() => undefined);
      setToast("User created. Invite code copied.");
    } catch (err) {
      setToast(err.message);
    }
  }

  function openUser(user) {
    setSelectedUser(user);
    setEditName(user.name || "");
    setEditPhone(user.employeeNumber || "");
    setEditEmail(user.email || "");
    setEditHourlyRate(Number(user.hourlyRate || 0));
  }

  async function updateUser() {
    try {
      await apiFetch(`/users/${selectedUser.id}`, {
        method: "PUT",
        body: {
          name: editName,
          employeeNumber: editPhone,
          email: editEmail,
          hourlyRate: Number(editHourlyRate || 0),
        },
        teamId,
      });

      clearUsersCache(teamId);
      setSelectedUser(null);
      setEditName("");
      setEditPhone("");
      setEditEmail("");
      setEditHourlyRate(0);
      await load();
      setToast("User updated.");
    } catch (err) {
      setToast(err.message);
    }
  }

  async function deleteUser() {
    if (!selectedUser) {
      return;
    }

    try {
      await apiFetch(`/users/${selectedUser.id}`, { method: "DELETE", teamId });
      clearUsersCache(teamId);
      setIsDeleteOpen(false);
      setSelectedUser(null);
      await load();
      setToast("User deleted.");
    } catch (err) {
      setIsDeleteOpen(false);
      setToast(err.message);
    }
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={() => history.goBack()} aria-label="Back">
              <IonIcon icon={chevronBack} />
            </IonButton>
          </IonButtons>
          <IonTitle>{team?.name || "Team Users"}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => setIsCreateOpen(true)} aria-label="Create user">
              <IonIcon icon={add} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen>
        <main className="page-pad">
          <section className="list-stack">
            {users.map((user) => (
              <article
                className="panel invite-card user-card list-button"
                key={user.id}
                role="button"
                tabIndex={0}
                onClick={() => openUser(user)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openUser(user);
                  }
                }}
              >
                <div className="row-card">
                  <div className="user-card-main">
                    <h3>{user.name}</h3>
                    <p>
                      {user.isOwner ? "Owner" : user.role?.name === "admin" ? "Admin" : user.employeeNumber || "Standard user"} ·{" "}
                      {user.isActive ? "Joined" : "Pending invitation"}
                    </p>
                    {user.email && <p>{user.email}</p>}
                  </div>
                  <div className="user-card-trailing">
                    <LastActive value={user.lastActive} />
                    {user.role?.name !== "admin" && (
                      <IonButton
                        fill="clear"
                        onClick={(event) => {
                          event.stopPropagation();
                          copyCode(user);
                        }}
                        aria-label={`Copy invite code for ${user.name}`}
                      >
                        <IonIcon icon={copyOutline} />
                      </IonButton>
                    )}
                  </div>
                </div>
                {user.role?.name !== "admin" && <span className="invite-code">{inviteCode(user)}</span>}
              </article>
            ))}
            {users.length === 0 && <div className="empty-state">No users in this team.</div>}
          </section>
        </main>

        <IonModal isOpen={isCreateOpen} onDidDismiss={() => setIsCreateOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Create User</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setIsCreateOpen(false)}>Close</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <main className="page-pad">
              <section className="panel form-grid">
                <IonInput
                  className="metora-input"
                  label="Name"
                  labelPlacement="stacked"
                  placeholder="Enter employee name"
                  value={form.name}
                  onIonInput={(event) => setForm({ ...form, name: event.detail.value ?? "" })}
                />
                <IonInput
                  className="metora-input"
                  label="Employee Number"
                  labelPlacement="stacked"
                  placeholder="Enter employee number"
                  value={form.employeeNumber}
                  onIonInput={(event) => setForm({ ...form, employeeNumber: event.detail.value ?? "" })}
                />
                <IonInput
                  className="metora-input"
                  label="Email"
                  labelPlacement="stacked"
                  type="email"
                  placeholder="Enter employee email"
                  value={form.email}
                  onIonInput={(event) => setForm({ ...form, email: event.detail.value ?? "" })}
                />
                <IonInput
                  className="metora-input"
                  label="Hourly Rate"
                  labelPlacement="stacked"
                  type="number"
                  placeholder="Enter hourly rate"
                  value={form.hourlyRate}
                  onIonInput={(event) => setForm({ ...form, hourlyRate: event.detail.value ?? 0 })}
                />
                <IonButton expand="block" onClick={save} disabled={!form.name.trim()}>
                  Create User
                </IonButton>
              </section>
            </main>
          </IonContent>
        </IonModal>

        <IonModal isOpen={Boolean(selectedUser)} onDidDismiss={() => setSelectedUser(null)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Manage User</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setSelectedUser(null)}>Close</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <main className="page-pad">
              <section className="panel form-grid">
                <IonInput
                  className="metora-input"
                  label="Name"
                  labelPlacement="stacked"
                  value={editName}
                  onIonInput={(event) => setEditName(event.detail.value ?? "")}
                />
                <IonInput
                  className="metora-input"
                  label="Phone Number"
                  labelPlacement="stacked"
                  type="tel"
                  placeholder="Enter phone number"
                  value={editPhone}
                  onIonInput={(event) => setEditPhone(event.detail.value ?? "")}
                />
                <IonInput
                  className="metora-input"
                  label="Email"
                  labelPlacement="stacked"
                  type="email"
                  placeholder="Enter email"
                  value={editEmail}
                  onIonInput={(event) => setEditEmail(event.detail.value ?? "")}
                />
                {selectedUser?.isOwner && (
                  <label className="checkbox-row input-readonly">
                    <IonCheckbox checked disabled />
                    <span>Team Owner</span>
                  </label>
                )}
                {!selectedUser?.isOwner && (
                  <IonInput
                    className="metora-input"
                    label="Hourly Rate"
                    labelPlacement="stacked"
                    type="number"
                    placeholder="Enter hourly rate"
                    value={editHourlyRate}
                    onIonInput={(event) => setEditHourlyRate(event.detail.value ?? 0)}
                  />
                )}
                <IonButton expand="block" onClick={updateUser}>
                  Update User
                </IonButton>
                {selectedUser?.role?.name !== "admin" && (
                  <IonButton expand="block" color="danger" onClick={() => setIsDeleteOpen(true)}>
                    Delete User
                  </IonButton>
                )}
              </section>
            </main>
          </IonContent>
        </IonModal>

        <IonAlert
          cssClass="metora-alert"
          isOpen={isDeleteOpen}
          header="Delete User"
          message={`Delete ${selectedUser?.name || "this user"}? This removes the user from the team and cannot be undone.`}
          buttons={[
            { text: "Keep", role: "cancel" },
            { text: "Delete User", role: "destructive", handler: deleteUser },
          ]}
          onDidDismiss={() => setIsDeleteOpen(false)}
        />

        <IonToast isOpen={Boolean(toast)} message={toast} duration={2500} onDidDismiss={() => setToast("")} />
      </IonContent>
    </IonPage>
  );
}
